// 「どこに何があるか」の検索。メニュー項目と、各画面の中にある機能（設定のカード・画面内の欄など）を部分一致で探す。
// 画面に出すのは、その人がメニューで見られる画面の中のものだけ（権限・組織の非表示に従う）。
import type { Role } from "./types";
import type { Capability } from "./permissions";
import type { NavGroup, NavViewer } from "./nav";

export type SearchAction = "theme" | "password";

export interface SearchSection {
  label: string;
  path: string[];          // 表示用の場所（例：["設定", "補助金プラン管理"]）
  to?: string;             // 開く画面（#アンカー・?クエリ可）
  action?: SearchAction;   // 画面ではなく操作（表示設定・パスワード変更）
  parent?: string;         // この画面がメニューで見られる人にだけ出す（メニューの to）
  cap?: Capability;
  roles?: Role[];
  hint?: string;
  keywords?: string[];
}

export interface SearchEntry extends SearchSection { id: string }

/** 全角/半角・大文字/小文字・カタカナ/ひらがな・空白や「・」の違いを吸収する。 */
export function normalizeForSearch(s: string): string {
  return s
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[ァ-ヶ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x60))
    .replace(/[\s・･/／、,]+/g, "");
}

// メニュー項目ごとの言い換え（画面名に出てこない言葉でも見つかるように）
export const NAV_KEYWORDS: Record<string, string[]> = {
  "/": ["ホーム", "トップ", "決済状況サマリー"],
  "/exec": ["経営", "売上", "入金", "回収", "未入金"],
  "/locoreach-analytics": ["ロコリーチ", "解約", "継続率", "KPI", "プラン別", "担当者別"],
  "/operations": ["総務", "業務一覧"],
  "https://meoreport.camomile.co.jp/": ["MEO", "レポート", "Googleビジネスプロフィール"],
  "/follow-ups": ["連絡", "フォロー", "連絡忘れ"],
  "/sales": ["営業", "スタッフ別", "実績"],
  "/opportunities": ["商談", "パイプライン", "案件化", "成約"],
  "/collab": ["協業", "面談", "議事録"],
  "/sns-leads": ["SNS", "インスタ", "Instagram", "アプローチ"],
  "/sales-reports": ["月次報告", "目標", "実績", "日報"],
  "/expenses": ["経費", "レシート", "領収書", "精算"],
  "/deals": ["案件", "契約", "紹介"],
  "/subsidy": ["補助金", "IT導入補助金", "デジタル化・AI導入補助金", "加点", "公募", "交付申請", "インボイス枠"],
  "/contracts": ["電子契約", "署名", "サイン", "契約書PDF"],
  "/d-leads": ["紹介先", "リード", "アポ", "アポイント"],
  "/companies": ["法人", "事業者", "会社"],
  "/customers": ["顧客", "店舗", "お客様"],
  "/partners": ["紹介者", "代理店", "紹介料", "振込先"],
  "/vendors": ["提案企業"],
  "/store-payments": ["決済", "クレジットカード", "VeriTrans", "Paid", "請求書払い", "月次決済"],
  "/payments": ["請求書", "支払"],
  "/payroll": ["給与", "支払通知書", "インセン", "固定給", "集計表"],
  "/my-payslip": ["支払通知書", "明細", "自分の給与"],
  "/approvals": ["承認"],
  "/refunds": ["返金", "相殺", "キャンセル"],
  "/closings": ["締め", "月次締め"],
  "/rules": ["報酬", "紹介料のルール", "歩合"],
  "/notification-settings": ["通知", "プッシュ通知", "お知らせ"],
  "/notifications/send": ["お知らせを送る", "一斉通知"],
  "/storage": ["資料", "ファイル", "提案書", "契約書", "納品書", "督促状", "テンプレート", "PDF"],
  "/domains": ["ドメイン", "メール設定", "DNS", "更新日"],
  "/interviewees": ["面接", "採用", "契約形態", "面接判定", "保留", "質問テンプレート", "ヒアリングシート", "履歴書", "職務経歴書", "議事録", "AI自動入力"],
  "/tasks": ["タスク", "やること"],
  "/import": ["取込", "インポート", "CSV", "スプレッドシート"],
  "/audit": ["監査", "ログ", "操作履歴"],
  "/trash": ["ゴミ箱", "削除", "復元", "元に戻す"],
  "/users": ["ユーザー", "アカウント", "権限", "スタッフ", "招待"],
  "/settings": ["設定"],
};

// 画面の中にある機能（どの画面のどこにあるか）
export const SEARCH_SECTIONS: SearchSection[] = [
  // ---- 自分の画面（全員）----
  { label: "表示・テーマ（自分の画面）", path: ["アカウント", "表示・テーマ"], action: "theme", hint: "サイドメニューの色・項目ごとの表示と色（自分の画面だけ）", keywords: ["色", "テーマ", "カラー", "サイドメニュー", "非表示", "メニューを隠す"] },
  { label: "パスワード変更", path: ["アカウント", "パスワード変更"], action: "password", keywords: ["パスワード", "ログイン"] },
  // ---- 設定 ----
  { label: "紹介有効期限・データ保持期間など", path: ["設定", "基本設定"], to: "/settings#basic", parent: "/settings", keywords: ["紹介有効期限", "振込先変更", "データ保持", "支払予定日", "接近通知", "ステータス停滞"] },
  { label: "費目管理", path: ["設定", "費目管理"], to: "/settings#fee-items", parent: "/settings", keywords: ["TUSG管理費", "開発保守費", "粗利", "コスト"] },
  { label: "補助金プラン管理", path: ["設定", "補助金プラン管理"], to: "/settings#subsidy-plans", parent: "/settings", keywords: ["ミニマム", "ライト", "ベーシック", "ロイヤル", "補助金額", "実質負担"] },
  { label: "加点ルール管理", path: ["設定", "加点ルール管理"], to: "/settings#subsidy-rules", parent: "/settings", keywords: ["加点", "申請要件", "公募要領", "賃上げ", "インボイス登録"] },
  { label: "補助金 公募回スケジュール", path: ["設定", "公募回スケジュール"], to: "/settings#subsidy-rounds", parent: "/settings", keywords: ["締切", "公募回", "交付決定", "実績報告期限"] },
  { label: "支払可否チェック項目", path: ["設定", "支払可否チェック項目"], to: "/settings#payment-requirements", parent: "/settings", keywords: ["支払可否", "チェックリスト"] },
  { label: "自動ログアウト", path: ["設定", "自動ログアウト"], to: "/settings#auto-logout", parent: "/settings", keywords: ["ログアウト", "タイムアウト"] },
  { label: "APIトークン", path: ["設定", "APIトークン"], to: "/settings#api-tokens", parent: "/settings", keywords: ["API", "連携"] },
  { label: "追加項目の管理（LOCOREACH/店舗案件）", path: ["設定", "追加項目の管理"], to: "/settings#custom-fields", parent: "/settings", roles: ["owner", "admin"], keywords: ["項目", "カスタム項目"] },
  { label: "サイドメニューのカスタマイズ（全員）", path: ["設定", "サイドメニューのカスタマイズ"], to: "/settings#nav-customize", parent: "/settings", roles: ["owner", "admin"], hint: "全員のメニューの表示・名称・並び順", keywords: ["メニュー", "並び順", "名称"] },
  { label: "MEO管理ボード連携", path: ["設定", "MEO管理ボード連携"], to: "/settings#meo", parent: "/settings", keywords: ["MEO", "同期"] },
  { label: "LINE公式アカウント", path: ["設定", "LINE公式アカウント"], to: "/settings#line", parent: "/settings", keywords: ["LINE", "チャネル"] },
  { label: "Googleシート連携（案件を自動取込）", path: ["設定", "Googleシート連携"], to: "/settings#sheet-sync", parent: "/settings", keywords: ["スプレッドシート", "シート", "自動取込"] },
  { label: "取込：デリバリー入金明細", path: ["設定", "デリバリー入金明細"], to: "/settings#import-delivery", parent: "/settings", keywords: ["デリバリー", "入金明細", "確定入金"] },
  { label: "取込：DGFT会員ID 一括インポート", path: ["設定", "DGFT会員ID"], to: "/settings#dgft", parent: "/settings", keywords: ["DGFT", "会員ID"] },
  // ---- 給与計算 ----
  { label: "支払通知書の発行会社（ロゴ・社印・登録番号）", path: ["給与計算", "支払通知書の発行会社"], to: "/payroll", parent: "/payroll", roles: ["owner"], hint: "給与計算の「支払通知書」タブ →「支払通知書の発行会社」", keywords: ["ロゴ", "社印", "角印", "印鑑", "登録番号", "インボイス番号", "発行会社", "TUSG", "camomile"] },
  { label: "固定給マスタ", path: ["給与計算", "固定給マスタ"], to: "/payroll", parent: "/payroll", roles: ["owner"], keywords: ["固定給", "給与マスタ"] },
  { label: "経費・インセン集計表", path: ["給与計算", "集計表"], to: "/payroll", parent: "/payroll", keywords: ["インセンティブ", "集計表", "特別手当", "戻入"] },
  // ---- 資料格納 ----
  { label: "督促状・最終通告書をつくる", path: ["資料格納", "新規作成", "督促状・最終通告書"], to: "/storage", parent: "/storage", hint: "資料格納の「新規作成」から", keywords: ["督促", "督促状", "最終通告", "催告", "未払い", "支払いのお願い", "法的手続", "遅延損害金"] },
  { label: "LOCOREACH 提案書（販売プランのご案内）をつくる", path: ["資料格納", "新規作成", "LOCOREACH 提案書"], to: "/storage", parent: "/storage", hint: "資料格納の「新規作成」から", keywords: ["提案書", "販売プラン", "見積"] },
  { label: "ホームページ 契約書をつくる", path: ["資料格納", "新規作成", "ホームページ 契約書"], to: "/storage", parent: "/storage", hint: "資料格納の「新規作成」から", keywords: ["ホームページ", "契約書", "クラウドサイン", "制作・保守"] },
  { label: "ホームページ 納品書（検収書）をつくる", path: ["資料格納", "新規作成", "ホームページ 納品書（検収書）"], to: "/storage", parent: "/storage", hint: "資料格納の「新規作成」から", keywords: ["ホームページ", "納品書", "検収", "検収書", "クラウドサイン", "不具合", "バグ"] },
  { label: "ファイルをアップロード（資料格納）", path: ["資料格納", "ファイルをアップロード"], to: "/storage", parent: "/storage", keywords: ["アップロード", "PDF", "マニュアル"] },
  // ---- 補助金 ----
  { label: "補助金の申請の流れ・進捗・締切", path: ["補助金申請", "案件を開く", "申請の流れ"], to: "/subsidy", parent: "/subsidy", keywords: ["工程", "進捗", "締切", "交付決定", "実績報告", "効果報告"] },
  { label: "補助額の目安（試算）", path: ["補助金申請", "案件を開く", "補助額の目安"], to: "/subsidy", parent: "/subsidy", keywords: ["補助率", "補助額", "試算", "実質負担"] },
  { label: "交付申請に必要な書類", path: ["補助金申請", "案件を開く", "必要書類"], to: "/subsidy", parent: "/subsidy", keywords: ["必要書類", "納税証明書", "履歴事項全部証明書", "確定申告書"] },
  { label: "賃上げ加点・事前確認シート", path: ["補助金申請", "案件を開く", "事前確認シート"], to: "/subsidy", parent: "/subsidy", keywords: ["賃上げ", "最低賃金", "事前確認"] },
  { label: "申請要件・加点診断", path: ["補助金申請", "案件を開く", "加点診断"], to: "/subsidy", parent: "/subsidy", keywords: ["加点", "申請要件", "診断"] },
  // ---- 案件 ----
  { label: "案件を登録する", path: ["案件", "新規登録"], to: "/deals/new", parent: "/deals", cap: "deal.create", keywords: ["新規案件", "登録", "紹介を登録"] },
  { label: "補助金案件を登録する", path: ["補助金申請", "補助金案件を登録"], to: "/deals/new?type=subsidy", parent: "/subsidy", cap: "deal.create", keywords: ["補助金案件", "新規"] },
  { label: "支払可否判定・入出金・期日（案件ごと）", path: ["案件", "案件を開く"], to: "/deals", parent: "/deals", hint: "案件一覧から案件を開く", keywords: ["支払可否", "入金", "期日", "紹介料", "金額ロック"] },
  { label: "業務委託契約・請求書・支払承認・証拠パッケージ（補助金案件）", path: ["案件", "補助金案件を開く"], to: "/deals", parent: "/deals", hint: "補助金案件の案件詳細の下部", keywords: ["業務委託契約", "請求書", "支払承認", "証拠", "還流", "時系列"] },
  // ---- 営業 ----
  { label: "営業設定（スタッフ・報告項目・種別・選択肢）", path: ["営業報告", "営業設定"], to: "/sales-settings", cap: "sales_mgmt.config", keywords: ["報告項目", "スタッフ管理", "種別管理", "商談の選択肢", "提案ステータス", "日報の選択肢", "SNS案件の項目"] },
  { label: "商談結果を取込", path: ["商談", "商談結果を取込"], to: "/opportunities", parent: "/opportunities", keywords: ["取込", "LOCOREACH商談"] },
  // ---- 決済・分析 ----
  { label: "月次決済CSV・Paid明細PDFの取込", path: ["決済状況", "取込"], to: "/store-payments", parent: "/store-payments", keywords: ["CSV", "Paid", "明細", "未取得店舗"] },
  { label: "日々連絡顧客", path: ["顧客（店舗）", "日々連絡顧客"], to: "/customers?daily=1", parent: "/customers", keywords: ["日々連絡", "毎日"] },
  { label: "アポイントの事前通知", path: ["通知設定", "アポイントの事前通知"], to: "/notification-settings", parent: "/notification-settings", keywords: ["何分前", "リマインド", "アポ"] },
];

/** 見られるメニュー（permittedNav の結果）から、検索対象を作る。 */
export function buildSearchEntries(groups: NavGroup[], viewer: NavViewer): SearchEntry[] {
  const visible = new Set(groups.flatMap((g) => g.items.map((i) => i.to)));
  const navEntries: SearchEntry[] = groups.flatMap((g) => g.items.map((i) => ({
    id: `nav:${i.to}`,
    label: i.label,
    path: [g.label ?? "メニュー", i.label],
    to: i.to,
    keywords: NAV_KEYWORDS[i.to] ?? [],
  })));
  const sections: SearchEntry[] = SEARCH_SECTIONS
    .filter((s) => (!s.parent || visible.has(s.parent)) && (!s.cap || viewer.hasCap(s.cap)) && (!s.roles || s.roles.some((r) => viewer.hasRole(r))))
    .map((s, i) => ({ ...s, id: `sec:${i}` }));
  return [...navEntries, ...sections];
}

/** 部分一致で探す。空白区切りは「すべて含む」。名前の一致 > 場所の一致 > 言い換えの一致 の順。 */
export function searchEntries(entries: SearchEntry[], query: string, limit = 12): SearchEntry[] {
  const terms = query.split(/[\s　]+/).map(normalizeForSearch).filter(Boolean);
  if (!terms.length) return [];
  const scored: { e: SearchEntry; score: number }[] = [];
  for (const e of entries) {
    const label = normalizeForSearch(e.label);
    const path = normalizeForSearch(e.path.join(""));
    const kws = (e.keywords ?? []).map(normalizeForSearch);
    let score = 0;
    let ok = true;
    for (const t of terms) {
      if (label.startsWith(t)) score += 100;
      else if (label.includes(t)) score += 80;
      else if (path.includes(t)) score += 50;
      else if (kws.some((k) => k.includes(t))) score += 30;
      else { ok = false; break; }
    }
    if (ok) scored.push({ e, score: score - label.length / 100 + (e.id.startsWith("nav:") ? 5 : 0) });
  }
  return scored.sort((a, b) => b.score - a.score).slice(0, limit).map((x) => x.e);
}
